import React from 'react'

import classNames from 'classnames'

import useLDFlags from 'data/hooks/useLDFlags'
import { BaseAttribute } from 'features/views/attributes/editable/BaseAttribute'
import { useAttributeContext } from 'features/views/attributes/editable/hooks/useAttributeContext'
import { InlineEditingAttributeContextProvider } from 'features/views/attributes/editable/InlineEditingAttributeContextProvider'
import { TextAttributeEditor } from 'features/views/attributes/editable/TextAttribute'
import { EditableAttribute } from 'features/views/attributes/editable/types'
import { LongTextAttributeDisplay } from 'features/views/attributes/LongTextAttributeDisplay'
import { TextAttributeDisplay } from 'features/views/attributes/TextAttributeDisplay'

import { Headline } from 'ui/components/Text'
import { body } from 'ui/components/Text/Body.css'
import { headline } from 'ui/components/Text/Headline.css'
import { useResponsiveValue } from 'ui/styling/helpers/useResponsiveValue'

import { useDetailViewTitleAttributeState } from './hooks/useDetailViewTitleAttributeState'

import { DetailViewTitleAttributeStyles } from './DetailViewTitleAttribute.css'

const MAX_LINES = 1

type Size = React.ComponentPropsWithoutRef<typeof Headline>['size']

type DetailViewTitleAttributeProps = {
    variant?: 'headline' | 'body'
    size?: Size
    isLoading?: boolean
}

export const DetailViewTitleAttribute: React.FC<DetailViewTitleAttributeProps> = React.memo(
    function DetailViewTitleAttribute(props) {
        const { field, overrideIsEditable } = useDetailViewTitleAttributeState()
        if (!field) return null

        return (
            <InlineEditingAttributeContextProvider
                field={field}
                overrideIsEditable={overrideIsEditable}
            >
                <DetailViewTitleAttributeInner field={field} {...props} />
            </InlineEditingAttributeContextProvider>
        )
    }
)

type DetailViewTitleAttributeInnerProps = DetailViewTitleAttributeProps & {
    field: EditableAttribute
}

const DetailViewTitleAttributeInner: React.FC<DetailViewTitleAttributeInnerProps> = ({
    field,
    size = 'm',
    variant = 'body',
    isLoading,
}) => {
    const effectiveSize = useResponsiveValue(size)
    const { value, isEditingValue, isEditable: attributeIsEditable } = useAttributeContext<string>()

    const { flags } = useLDFlags()
    const isEditable = flags.detailViewHeaderInlineEditing && attributeIsEditable

    const isEmpty = !isLoading && !value
    const className = classNames(
        DetailViewTitleAttributeStyles.styleFunction({ variant }),
        variant === 'headline' && headline.styleFunction({ size: effectiveSize }),
        variant === 'body' && body.styleFunction({ size: effectiveSize as any, weight: 'regular' })
    )

    if (!isEditable && isEmpty) return null

    if (isEditingValue) {
        return (
            <TextAttributeEditor
                field={field}
                isLoading={isLoading}
                labelPlacement="hide"
                className={className}
                showFieldIcon={false}
            />
        )
    }

    return (
        <BaseAttribute
            field={field}
            isEmpty={isEmpty}
            isLoading={isLoading}
            cursor={isEditable ? 'text' : undefined}
            labelPlacement="hide"
            className={className}
            hideClearValueButton={true}
            showFieldIcon={false}
        >
            <DetailViewTitleAttributeContent
                field={field}
                value={value}
                isLoading={isLoading}
                style={{
                    fontSize: 'inherit',
                    lineHeight: 'inherit',
                    letterSpacing: 'inherit',
                    fontFamily: 'inherit',
                    fontWeight: 'inherit',
                }}
            />
        </BaseAttribute>
    )
}

type DetailViewTitleAttributeContentProps = {
    field: EditableAttribute
    value: unknown
    style?: React.CSSProperties
    className?: string
    isLoading?: boolean
}

const DetailViewTitleAttributeContent: React.FC<DetailViewTitleAttributeContentProps> = React.memo(
    function DetailViewTitleAttributeContent({ field, value, ...props }) {
        switch (field.field.type) {
            case 'string':
                return (
                    <TextAttributeDisplay
                        field={field.field}
                        value={value as string}
                        alwaysShowOverflowTooltip
                        maxLines={MAX_LINES}
                        {...props}
                    />
                )

            case 'long_text':
                return (
                    <LongTextAttributeDisplay
                        field={field.field}
                        value={value as string}
                        alwaysShowOverflowTooltip
                        maxLines={MAX_LINES}
                        {...props}
                    />
                )

            default:
                return null
        }
    }
)
